import { useState, useCallback, useEffect, useRef } from 'react';
import { useAuthInfo } from '@propelauth/react';
import useWorkspaceStore from '../../../../../store/workspace/workspaceStore';
import useAIResponseStream from '../../../../../components/agno/hooks/useAIResponseStream';
import APIRoutes from '../../../../../components/agno/vars';
import { constructEndpointUrl } from '../../../../../lib/constructEndpointUrl';
import { RunEvent } from '../../../../../types/os';
import useStore from '../../../../../components/agno/store';
import { mergeStreamingContent } from '../../../../../lib/mergeStreamingContent';
import { queryClient } from '../../../../../hooks/QueryHooks';

export interface SignalBuilderMessage {
  id: string;
  role: 'user' | 'assistant' | 'tool';
  content: string;
  isLoading?: boolean;
  toolCall?: {
    tool_call_id?: string;
    tool_name: string;
    result: any;
    args?: Record<string, unknown> | string | null;
    status?: 'running' | 'complete' | 'error';
    error?: string;
  };
}

interface UseSignalBuilderStreamOptions {
  draftSignalConfig?: Record<string, unknown> | null;
}

interface SendSignalBuilderMessageOptions {
  draftOverride?: Record<string, unknown> | null;
}

export const INITIAL_SIGNAL_BUILDER_MESSAGE: SignalBuilderMessage = {
  id: 'initial',
  role: 'assistant',
  content: `Hi! I'm ORA and I'll help you create a new signal step by step.

**What would you like to call this signal?**

Give it a short, descriptive name (e.g. "At-Risk Accounts", "Low Engagement Alert").`,
};

/**
 * Parse tool result that may arrive as a Python-style string representation.
 * Mirrors the parseToolResult in useAIChatStreamHandler.ts.
 */
function parseToolResult(result: any): any {
  if (typeof result !== 'string') return result;
  try {
    return JSON.parse(result);
  } catch {
    try {
      let fixed = result;
      fixed = fixed.replace(/UUID\('([^']+)'\)/g, '"$1"');
      fixed = fixed.replace(
        /datetime\.datetime\((\d+),\s*(\d+),\s*(\d+),\s*(\d+),\s*(\d+),\s*(\d+),\s*(\d+)[^)]*\)/g,
        (_: string, year: string, month: string, day: string, hour: string, minute: string, second: string, microsecond: string) =>
          `"${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}T${hour.padStart(2, '0')}:${minute.padStart(2, '0')}:${second.padStart(2, '0')}.${microsecond.padStart(6, '0').slice(0, 3)}Z"`
      );
      fixed = fixed
        .replace(/\bNone\b/g, 'null')
        .replace(/\bTrue\b/g, 'true')
        .replace(/\bFalse\b/g, 'false');
      fixed = fixed.replace(/\\'/g, '___ESCAPED_QUOTE___');
      fixed = fixed.replace(/'/g, '"');
      fixed = fixed.replace(/___ESCAPED_QUOTE___/g, '\\"');
      return JSON.parse(fixed);
    } catch {
      return result;
    }
  }
}

const EMPTY_SIGNAL_BUILDER_DRAFT_HASH = '__signal_builder_draft_empty__';

function createSignalBuilderSessionId(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }

  return `signal-builder-${Date.now()}-${Math.random().toString(16).slice(2)}`;
}

function serializeSignalBuilderDraft(
  draft: Record<string, unknown> | null | undefined,
): string {
  if (!draft || Object.keys(draft).length === 0) {
    return EMPTY_SIGNAL_BUILDER_DRAFT_HASH;
  }

  try {
    return JSON.stringify(draft);
  } catch {
    return `signal-builder-draft-${Date.now()}`;
  }
}

export default function useSignalBuilderStream(options?: UseSignalBuilderStreamOptions) {
  const draftSignalConfig = options?.draftSignalConfig;
  const [messages, setMessages] = useState<SignalBuilderMessage[]>([
    INITIAL_SIGNAL_BUILDER_MESSAGE,
  ]);
  const [isStreaming, setIsStreaming] = useState(false);
  const [isAssistantTyping, setIsAssistantTyping] = useState(false);
  const [sessionId, setSessionId] = useState<string | null>(null);
  const sessionIdRef = useRef<string | null>(null);
  const pendingMessageRef = useRef<{
    text: string;
    options?: SendSignalBuilderMessageOptions;
  } | null>(null);
  const draftSyncTimeoutRef = useRef<number | null>(null);
  const lastSyncedDraftRef = useRef<string>(EMPTY_SIGNAL_BUILDER_DRAFT_HASH);

  const { streamResponse } = useAIResponseStream();
  const authInfo: any = useAuthInfo();
  const workspaceId =
    useWorkspaceStore((s) => s.workspace?.id) ||
    (typeof window !== 'undefined' ? localStorage.getItem('he_defws') : null);
  const selectedEndpoint = useStore((s) => s.selectedEndpoint);
  const modelConfig = useStore((s) => s.modelConfig);
  const sessionStorageKey = workspaceId ? `he_signal_builder_session:${workspaceId}` : 'he_signal_builder_session';

  useEffect(() => {
    if (typeof window === 'undefined') return;
    const saved = localStorage.getItem(sessionStorageKey);
    const nextSessionId = saved || createSignalBuilderSessionId();
    sessionIdRef.current = nextSessionId;
    setSessionId(nextSessionId);

    if (!saved) {
      localStorage.setItem(sessionStorageKey, nextSessionId);
    }
  }, [sessionStorageKey]);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    sessionIdRef.current = sessionId;
    if (sessionId) {
      localStorage.setItem(sessionStorageKey, sessionId);
    }
  }, [sessionId, sessionStorageKey]);

  const buildRequestHeaders = useCallback((activeSessionId: string | null) => {
    const headers: Record<string, string> = { 'Content-Type': 'application/json' };
    if (authInfo?.accessToken) headers.Authorization = `Bearer ${authInfo.accessToken}`;
    if (authInfo?.user?.userId) headers['X-User-Id'] = authInfo.user.userId;
    if (workspaceId) headers['X-Workspace-Id'] = String(workspaceId);
    if (activeSessionId) headers['X-Session-Id'] = activeSessionId;
    return headers;
  }, [authInfo?.accessToken, authInfo?.user?.userId, workspaceId]);

  const syncSignalBuilderDraft = useCallback(
    async (draftOverride?: Record<string, unknown> | null) => {
      const activeSessionId = sessionIdRef.current || sessionId;
      if (!workspaceId || !activeSessionId) return false;

      const nextDraft = draftOverride && Object.keys(draftOverride).length > 0 ? draftOverride : null;
      const nextDraftHash = serializeSignalBuilderDraft(nextDraft);
      if (lastSyncedDraftRef.current === nextDraftHash) {
        return true;
      }

      const endpointUrl = constructEndpointUrl(selectedEndpoint);
      const draftUrl = APIRoutes.SignalBuilderDraft(endpointUrl);

      try {
        const response = await fetch(draftUrl, {
          method: nextDraft ? 'PUT' : 'DELETE',
          headers: buildRequestHeaders(activeSessionId),
          ...(nextDraft ? { body: JSON.stringify({ draft: nextDraft }) } : {}),
        });

        if (!response.ok) {
          throw new Error(`Signal builder draft sync failed with status ${response.status}`);
        }

        lastSyncedDraftRef.current = nextDraftHash;
        return true;
      } catch (error) {
        console.error('Failed to sync signal builder draft', error);
        return false;
      }
    },
    [buildRequestHeaders, selectedEndpoint, sessionId, workspaceId],
  );

  useEffect(() => {
    if (!sessionId) return;

    if (draftSyncTimeoutRef.current !== null) {
      window.clearTimeout(draftSyncTimeoutRef.current);
    }

    draftSyncTimeoutRef.current = window.setTimeout(() => {
      void syncSignalBuilderDraft(draftSignalConfig ?? null);
    }, 250);

    return () => {
      if (draftSyncTimeoutRef.current !== null) {
        window.clearTimeout(draftSyncTimeoutRef.current);
        draftSyncTimeoutRef.current = null;
      }
    };
  }, [draftSignalConfig, sessionId, syncSignalBuilderDraft]);

  // Auto-send queued message once streaming finishes
  useEffect(() => {
    if (!isStreaming && pendingMessageRef.current) {
      const queued = pendingMessageRef.current;
      pendingMessageRef.current = null;
      // Use setTimeout to avoid calling sendMessage during render cycle
      setTimeout(() => sendMessage(queued.text, queued.options), 0);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isStreaming]);

  const sendMessage = useCallback(
    async (text: string, sendOptions?: SendSignalBuilderMessageOptions) => {
      if (!text.trim()) return;
      if (isStreaming) {
        // Queue the message to be sent once the current stream finishes
        pendingMessageRef.current = { text, options: sendOptions };
        return;
      }

      const userMsg: SignalBuilderMessage = {
        id: Date.now().toString(),
        role: 'user',
        content: text,
      };

      const messageForBackend = text;

      const assistantMsg: SignalBuilderMessage = {
        id: (Date.now() + 1).toString(),
        role: 'assistant',
        content: '',
        isLoading: true,
      };

      setMessages((prev) => [...prev, userMsg, assistantMsg]);
      setIsStreaming(true);

      const endpointUrl = constructEndpointUrl(selectedEndpoint);
      const runUrl = APIRoutes.SkillsStream(endpointUrl);
      const activeSessionId = sessionIdRef.current || sessionId;
      const headers = buildRequestHeaders(activeSessionId);
      const draftForTurn =
        sendOptions?.draftOverride !== undefined ? sendOptions.draftOverride : draftSignalConfig ?? null;
      const draftSynced = await syncSignalBuilderDraft(draftForTurn);

      const requestBody: Record<string, unknown> = {
        message: messageForBackend,
        agent_id: "signal_builder",
        model_config: { model_key: modelConfig.model_key },
        state: {
          signal_builder_mode: true,
          ...(!draftSynced && draftForTurn ? { signal_builder_draft: draftForTurn } : {}),
        },
      };

      try {
        await streamResponse({
          apiUrl: runUrl,
          headers,
          requestBody,
          onChunk: (chunk: any) => {
            if (chunk.session_id) {
              const incomingSessionId = String(chunk.session_id);
              sessionIdRef.current = incomingSessionId;
              setSessionId((prev) => (prev === incomingSessionId ? prev : incomingSessionId));
            }

            if (chunk.event === RunEvent.FirstToken) {
              setIsAssistantTyping(true);
              setMessages((prev) => {
                const msgs = [...prev];
                const last = msgs[msgs.length - 1];
                if (last?.role === 'assistant') {
                  last.isLoading = false;
                  last.content = '';
                }
                return msgs;
              });
            } else if (
              chunk.event === RunEvent.RunContent ||
              chunk.event === RunEvent.TeamRunContent
            ) {
              if (typeof chunk.content === 'string') {
                setMessages((prev) => {
                  const msgs = [...prev];
                  const last = msgs[msgs.length - 1];

                  if (last?.role === 'assistant') {
                    // Stream into the current assistant bubble.
                    last.content = mergeStreamingContent(last.content || '', chunk.content as string);
                    last.isLoading = false;
                  } else if (last?.role === 'tool') {
                    // After a tool card, find the pre-tool assistant bubble
                    // and UPDATE it instead of creating a new one. This keeps
                    // one assistant bubble per turn and avoids duplication from
                    // cumulative payloads that concatenate pre+post tool text.
                    let targetIdx = -1;
                    for (let i = msgs.length - 1; i >= 0; i--) {
                      if (msgs[i].role === 'assistant') {
                        targetIdx = i;
                        break;
                      }
                      if (msgs[i].role === 'user') break; // don't cross turn boundary
                    }

                    if (targetIdx >= 0) {
                      // Replace the bubble content with incoming (which is
                      // cumulative full-turn text or new delta). mergeStreamingContent
                      // handles both cases correctly within a single bubble.
                      msgs[targetIdx].content = mergeStreamingContent(
                        msgs[targetIdx].content || '',
                        chunk.content as string,
                      );
                      msgs[targetIdx].isLoading = false;
                    } else {
                      // No prior assistant bubble found — create one
                      msgs.push({
                        id: `assistant-${Date.now()}-${Math.random()}`,
                        role: 'assistant',
                        content: chunk.content as string,
                        isLoading: false,
                      });
                    }
                  }
                  return msgs;
                });
              }
            } else if (
              chunk.event === RunEvent.ToolCallStarted ||
              chunk.event === RunEvent.TeamToolCallStarted
            ) {
              setIsAssistantTyping(false);
              const rawTool = chunk.tool;
              const toolName = rawTool?.tool_name || rawTool?.name || rawTool?.toolName || 'tool';
              const toolCallId = rawTool?.tool_call_id || rawTool?.id;
              const runningToolMsg: SignalBuilderMessage = {
                id: `tool-running-${Date.now()}-${Math.random()}`,
                role: 'tool',
                content: '',
                toolCall: {
                  tool_call_id: toolCallId,
                  tool_name: String(toolName),
                  result: null,
                  args: rawTool?.args || rawTool?.tool_args || null,
                  status: 'running',
                },
              };

              setMessages((prev) => {
                const msgs = [...prev];
                const last = msgs[msgs.length - 1];

                if (last?.role === 'assistant' && !last.content) {
                  return [...msgs.slice(0, -1), runningToolMsg];
                }

                if (
                  last?.role === 'tool' &&
                  last.toolCall?.status === 'running' &&
                  last.toolCall?.tool_name === toolName
                ) {
                  return msgs;
                }

                return [...msgs, runningToolMsg];
              });
            } else if (
              chunk.event === RunEvent.ToolCallCompleted ||
              chunk.event === RunEvent.TeamToolCallCompleted
            ) {
              // chunk.tool may have result as Python-serialized string — parse it.
              const rawTool = chunk.tool;
              if (!rawTool) return;

              const toolName = rawTool.tool_name || rawTool.name || rawTool.toolName || 'tool';
              const toolCallId = rawTool.tool_call_id || rawTool.id;
              const parsedResult = parseToolResult(rawTool.result);

              // If a signal was just created successfully, refresh the signals list
              if (
                parsedResult?.ui_step === 'signal_created' &&
                parsedResult?.success === true
              ) {
                queryClient.invalidateQueries({
                  predicate: (query) => {
                    const [queryKey, queryWorkspaceId, automationType] = query.queryKey;
                    return (
                      queryKey === 'all_automations' &&
                      automationType === 'signal' &&
                      String(queryWorkspaceId) === String(workspaceId)
                    );
                  },
                });
              }

              const toolMsg: SignalBuilderMessage = {
                id: `tool-${Date.now()}-${Math.random()}`,
                role: 'tool',
                content: '',
                toolCall: {
                  tool_call_id: toolCallId,
                  tool_name: toolName,
                  result: parsedResult,
                  args: rawTool.args || rawTool.tool_args || null,
                  status: 'complete',
                },
              };

              setMessages((prev) => {
                const msgs = [...prev];

                // Update the most recent matching running tool card first.
                for (let index = msgs.length - 1; index >= 0; index -= 1) {
                  const message = msgs[index];
                  if (
                    message.role === 'tool' &&
                    message.toolCall?.status === 'running' &&
                    (
                      (toolCallId && message.toolCall?.tool_call_id === toolCallId) ||
                      message.toolCall?.tool_name === toolName
                    )
                  ) {
                    msgs[index] = toolMsg;
                    return msgs;
                  }
                }

                // Replace trailing empty/loading assistant bubble with the tool card.
                const last = msgs[msgs.length - 1];
                if (last?.role === 'assistant' && !last.content && last.isLoading) {
                  return [...msgs.slice(0, -1), toolMsg];
                }
                return [...msgs, toolMsg];
              });
            } else if (chunk.event === RunEvent.RunCompletedEvent) {
              // Finalize the last assistant bubble — never create a new one.
              // RunCompletedEvent re-sends the full turn text (pre-tool + post-tool
              // concatenated) which was already streamed via RunContent, so creating
              // a new bubble here would duplicate the message.
              if (typeof chunk.content === 'string' && chunk.content.length > 0) {
                setMessages((prev) => {
                  const msgs = [...prev];
                  const last = msgs[msgs.length - 1];
                  if (last?.role === 'assistant') {
                    if (!last.content || (chunk.content as string).length >= last.content.length) {
                      last.content = chunk.content as string;
                    }
                    last.isLoading = false;
                  }
                  // If last message is a tool card, do nothing — the content was
                  // already streamed into an earlier assistant bubble via RunContent.
                  return msgs;
                });
              }
            } else if (
              chunk.event === RunEvent.RunCompleted ||
              chunk.event === RunEvent.TeamRunCompleted
            ) {
              // Finalize the last assistant bubble — never create a new one.
              // Same rationale as RunCompletedEvent above.
              setMessages((prev) => {
                const msgs = [...prev];
                const last = msgs[msgs.length - 1];
                if (last?.role === 'assistant') {
                  if (typeof chunk.content === 'string' && chunk.content.length > 0) {
                    if (!last.content || (chunk.content as string).length >= last.content.length) {
                      last.content = chunk.content as string;
                    }
                  }
                  last.isLoading = false;
                }
                // If last message is a tool card, do nothing — content was already
                // streamed into an earlier assistant bubble.
                return msgs;
              });
            }
          },
          onError: () => {
            setMessages((prev) => {
              const msgs = [...prev];
              const last = msgs[msgs.length - 1];
              if (last?.role === 'assistant') {
                last.content = 'Sorry, something went wrong. Please try again.';
                last.isLoading = false;
                return msgs;
              }

              return [
                ...msgs,
                {
                  id: `assistant-error-${Date.now()}`,
                  role: 'assistant',
                  content: 'Sorry, something went wrong. Please try again.',
                },
              ];
            });
          },
          onComplete: () => {
            setMessages((prev) => {
              const msgs = [...prev];
              const last = msgs[msgs.length - 1];
              if (last?.role === 'assistant') {
                last.isLoading = false;
              }
              return msgs;
            });
          },
        });
      } finally {
        setIsStreaming(false);
        setIsAssistantTyping(false);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [buildRequestHeaders, draftSignalConfig, isStreaming, modelConfig, selectedEndpoint, sessionId, streamResponse, syncSignalBuilderDraft]
  );

  const resetChat = useCallback(() => {
    void syncSignalBuilderDraft(null);
    setMessages([INITIAL_SIGNAL_BUILDER_MESSAGE]);
    const nextSessionId = createSignalBuilderSessionId();
    lastSyncedDraftRef.current = EMPTY_SIGNAL_BUILDER_DRAFT_HASH;
    sessionIdRef.current = nextSessionId;
    setSessionId(nextSessionId);
    if (typeof window !== 'undefined') {
      localStorage.setItem(sessionStorageKey, nextSessionId);
    }
  }, [sessionStorageKey, syncSignalBuilderDraft]);

  return { messages, isStreaming, isAssistantTyping, sendMessage, resetChat };
}
